if (!customElements.get('bundle-compra-mas')) {
  customElements.define(
    'bundle-compra-mas',
    class BundleCompraMas extends HTMLElement {
      constructor() {
        super();

        const dataEl = this.querySelector('[data-bundle-product-data]');
        if (!dataEl) return;
        this.productData = JSON.parse(dataEl.textContent);

        this.tiers = this.querySelectorAll('.bundle-compra-mas__tier');
        this.addButton = this.querySelector('.bundle-compra-mas__add-btn');
        this.addButtonText = this.addButton?.querySelector('.bundle-compra-mas__add-btn-text');
        this.addButtonSpinner = this.addButton?.querySelector('.bundle-compra-mas__spinner');
        this.mixToggle = this.querySelector('.bundle-compra-mas__mix-toggle');
        this.mixPanel = this.querySelector('.bundle-compra-mas__mix-panel');
        this.errorEl = this.querySelector('.bundle-compra-mas__error');

        this.currentVariantId = this.productData.currentVariantId;
        this.currentVariant = this.productData.variants.find((v) => v.id === this.currentVariantId);
        this.mixMode = false;
        this.activeTier = this.querySelector('.bundle-compra-mas__tier--active');

        this.setupTierSelection();
        this.setupMixToggle();
        this.setupAddToCart();
        this.subscribeToVariantChanges();

        this.updatePrices();
        this.updateButtonState();
      }

      /* ── Helpers ── */

      getTierInfo(tier) {
        const buyQty = parseInt(tier.dataset.buy);
        const freeQty = parseInt(tier.dataset.free);
        const discountType = tier.dataset.discountType || 'none';
        const discountPct = parseInt(tier.dataset.discountPercentage) || 0;
        const totalQty = discountType === 'free_unit' ? buyQty + freeQty : buyQty;
        return { buyQty, freeQty, discountType, discountPct, totalQty };
      }

      /* ── Tier Selection ── */

      setupTierSelection() {
        this.tiers.forEach((tier) => {
          tier.addEventListener('click', () => {
            this.tiers.forEach((t) => t.classList.remove('bundle-compra-mas__tier--active'));
            tier.classList.add('bundle-compra-mas__tier--active');
            this.activeTier = tier;

            if (this.mixMode) {
              this.renderMixSelectors();
            }
          });
        });
      }

      /* ── Mix Toggle ── */

      setupMixToggle() {
        if (!this.mixToggle || !this.mixPanel) return;

        this.mixToggleDefaultText = this.mixToggle.textContent.trim();

        this.mixToggle.addEventListener('click', () => {
          this.mixMode = !this.mixMode;

          if (this.mixMode) {
            this.mixPanel.removeAttribute('hidden');
            this.mixToggle.classList.add('bundle-compra-mas__mix-toggle--open');
            this.renderMixSelectors();
          } else {
            this.mixPanel.setAttribute('hidden', '');
            this.mixToggle.classList.remove('bundle-compra-mas__mix-toggle--open');
          }
        });
      }

      /* ── Add to Cart Setup ── */

      setupAddToCart() {
        if (!this.addButton) return;
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      /* ── Variant Changes ── */

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function') return;

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event.data?.variant) return;

          const variant = event.data.variant;
          this.currentVariantId = variant.id;
          this.currentVariant = this.productData.variants.find((v) => v.id === variant.id) || {
            id: variant.id,
            price: variant.price,
            available: variant.available,
            option1: variant.option1,
            option2: variant.option2,
            option3: variant.option3,
          };

          this.updatePrices();
          this.updateButtonState();
        });
      }

      /* ── Price Calculation ── */

      updatePrices() {
        if (!this.currentVariant) return;
        const price = this.currentVariant.price;

        this.tiers.forEach((tier) => {
          const { buyQty, freeQty, discountType, discountPct, totalQty } = this.getTierInfo(tier);

          let bundlePrice, originalPrice, savings;

          switch (discountType) {
            case 'percentage':
              originalPrice = price * totalQty;
              bundlePrice = Math.round(originalPrice * (1 - discountPct / 100));
              savings = originalPrice - bundlePrice;
              break;

            case 'free_unit':
              bundlePrice = price * buyQty;
              originalPrice = price * totalQty;
              savings = originalPrice - bundlePrice;
              break;

            case 'none':
            default:
              bundlePrice = price * buyQty;
              originalPrice = bundlePrice;
              savings = 0;
              break;
          }

          const bundlePriceEl = tier.querySelector('[data-bundle-price]');
          const originalPriceEl = tier.querySelector('[data-original-price]');
          const savingsEl = tier.querySelector('[data-savings]');

          if (bundlePriceEl) bundlePriceEl.textContent = this.formatMoney(bundlePrice);

          if (originalPriceEl) {
            if (savings > 0) {
              originalPriceEl.textContent = this.formatMoney(originalPrice);
              originalPriceEl.removeAttribute('hidden');
            } else {
              originalPriceEl.setAttribute('hidden', '');
            }
          }

          if (savingsEl) {
            if (savings > 0) {
              savingsEl.textContent = savingsEl.dataset.prefix + this.formatMoney(savings);
              savingsEl.removeAttribute('hidden');
            } else {
              savingsEl.setAttribute('hidden', '');
            }
          }
        });
      }

      /* ── Button State ── */

      updateButtonState() {
        if (!this.addButton) return;

        if (!this.currentVariant || !this.currentVariant.available) {
          this.addButton.disabled = true;
          if (this.addButtonText) this.addButtonText.textContent = 'AGOTADO';
        } else {
          this.addButton.disabled = false;
          if (this.addButtonText)
            this.addButtonText.textContent = this.addButton.dataset.defaultText || 'AGREGAR AL CARRITO';
        }
      }

      /* ── Mix Mode Selectors ── */

      renderMixSelectors() {
        if (!this.mixPanel || !this.activeTier) return;

        const { totalQty } = this.getTierInfo(this.activeTier);
        const options = this.productData.options;

        let html = '';

        for (let i = 0; i < totalQty; i++) {
          html += `<div class="bundle-compra-mas__mix-unit" data-unit-index="${i}">`;
          html += `<span class="bundle-compra-mas__mix-unit-label">Unidad ${i + 1}</span>`;
          html += `<div class="bundle-compra-mas__mix-selectors">`;

          options.forEach((option, optIndex) => {
            html += `<select class="bundle-compra-mas__mix-select" data-option-index="${optIndex}" data-unit="${i}" aria-label="${this.escapeHtml(option.name)} - Unidad ${i + 1}">`;
            option.values.forEach((value) => {
              const selected =
                this.currentVariant && this.currentVariant[`option${optIndex + 1}`] === value ? ' selected' : '';
              html += `<option value="${this.escapeHtml(value)}"${selected}>${this.escapeHtml(value)}</option>`;
            });
            html += `</select>`;
          });

          html += `</div>`;
          html += `<span class="bundle-compra-mas__mix-status" data-unit="${i}"></span>`;
          html += `</div>`;
        }

        this.mixPanel.innerHTML = html;

        this.mixPanel.querySelectorAll('.bundle-compra-mas__mix-select').forEach((select) => {
          select.addEventListener('change', () => {
            this.validateMixUnit(parseInt(select.dataset.unit));
          });
        });

        for (let i = 0; i < totalQty; i++) {
          this.validateMixUnit(i);
        }
      }

      /* ── Mix Validation ── */

      validateMixUnit(unitIndex) {
        const unit = this.mixPanel.querySelector(`[data-unit-index="${unitIndex}"]`);
        if (!unit) return;

        const selects = unit.querySelectorAll('.bundle-compra-mas__mix-select');
        const selectedOptions = [];
        selects.forEach((select) => selectedOptions.push(select.value));

        const variant = this.findVariant(selectedOptions);
        const statusEl = unit.querySelector('.bundle-compra-mas__mix-status');

        if (variant && variant.available) {
          statusEl.textContent = '\u2713';
          statusEl.className = 'bundle-compra-mas__mix-status bundle-compra-mas__mix-status--ok';
          unit.dataset.variantId = variant.id;
        } else {
          statusEl.textContent = variant ? 'Agotado' : 'No disponible';
          statusEl.className = 'bundle-compra-mas__mix-status bundle-compra-mas__mix-status--error';
          unit.dataset.variantId = '';
        }
      }

      findVariant(selectedOptions) {
        return this.productData.variants.find((variant) => {
          return selectedOptions.every((option, index) => {
            return variant[`option${index + 1}`] === option;
          });
        });
      }

      /* ── Add to Cart ── */

      async addToCart() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (this.addButton.disabled) return;

        this.hideError();
        this.addButton.classList.add('loading');
        this.addButton.disabled = true;
        if (this.addButtonSpinner) this.addButtonSpinner.removeAttribute('hidden');
        if (this.addButtonText) this.addButtonText.style.visibility = 'hidden';

        try {
          let response;
          const { totalQty } = this.getTierInfo(this.activeTier);

          if (this.mixMode) {
            // MIX MODE: multiple variants
            const units = this.mixPanel.querySelectorAll('.bundle-compra-mas__mix-unit');
            const items = [];
            let hasError = false;

            units.forEach((unit) => {
              const variantId = unit.dataset.variantId;
              if (!variantId) {
                hasError = true;
                return;
              }
              const existing = items.find((item) => item.id === parseInt(variantId));
              if (existing) {
                existing.quantity += 1;
              } else {
                items.push({ id: parseInt(variantId), quantity: 1 });
              }
            });

            if (hasError) {
              this.showError('Algunas combinaciones no estan disponibles. Revisa tu seleccion.');
              return;
            }

            const body = { items };
            if (this.cart) {
              body.sections = this.cart.getSectionsToRender().map((section) => section.id);
              body.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }

            response = await fetch(routes.cart_add_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(body),
            });
          } else {
            // NON-MIX MODE: single variant
            if (!this.currentVariantId) {
              this.showError('Selecciona una variante primero.');
              return;
            }

            const formData = new FormData();
            formData.append('id', this.currentVariantId);
            formData.append('quantity', totalQty);

            if (this.cart) {
              formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
              formData.append('sections_url', window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }

            response = await fetch(routes.cart_add_url, {
              method: 'POST',
              headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: formData,
            });
          }

          const data = await response.json();

          if (data.status) {
            this.showError(data.description || data.message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bundle-compra-mas',
              errors: data.errors || data.description,
              message: data.message,
            });
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-compra-mas',
            cartData: data,
          });

          if (this.cart) {
            try {
              // Normalize multi-item response for cart-notification compatibility
              if (data.items && !data.key) {
                data.key = data.items[0].key;
                data.id = data.items[0].id;
              }
              this.cart.renderContents(data);
            } catch (renderError) {
              console.error('Cart render error:', renderError);
              window.location = routes.cart_url;
            }
          } else {
            window.location = routes.cart_url;
          }
        } catch (error) {
          console.error('Bundle add to cart error:', error);
          this.showError('Error al agregar al carrito. Intenta de nuevo.');
        } finally {
          this.addButton.classList.remove('loading');
          this.addButton.disabled = false;
          if (this.addButtonSpinner) this.addButtonSpinner.setAttribute('hidden', '');
          if (this.addButtonText) this.addButtonText.style.visibility = '';
        }
      }

      /* ── Error Handling ── */

      showError(message) {
        this.addButton.classList.remove('loading');
        this.addButton.disabled = false;
        if (this.addButtonSpinner) this.addButtonSpinner.setAttribute('hidden', '');
        if (this.addButtonText) this.addButtonText.style.visibility = '';

        if (this.errorEl) {
          this.errorEl.textContent = message;
          this.errorEl.removeAttribute('hidden');
          clearTimeout(this._errorTimeout);
          this._errorTimeout = setTimeout(() => this.hideError(), 5000);
        }
      }

      hideError() {
        if (this.errorEl) {
          this.errorEl.setAttribute('hidden', '');
        }
      }

      /* ── Money Format ── */

      formatMoney(cents) {
        const moneyFormat = this.productData.moneyFormat;
        const intPart = Math.floor(cents / 100).toString();
        const decPart = (cents % 100).toString().padStart(2, '0');
        const amountNoDecimals = Math.round(cents / 100).toString();

        function withDelimiter(numStr, delimiter) {
          return numStr.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + delimiter);
        }

        return moneyFormat
          .replace('{{amount_with_comma_separator}}', withDelimiter(intPart, '.') + ',' + decPart)
          .replace('{{amount_no_decimals_with_comma_separator}}', withDelimiter(amountNoDecimals, '.'))
          .replace("{{amount_with_apostrophe_separator}}", withDelimiter(intPart, "'") + '.' + decPart)
          .replace("{{amount_no_decimals_with_apostrophe_separator}}", withDelimiter(amountNoDecimals, "'"))
          .replace('{{amount_no_decimals}}', withDelimiter(amountNoDecimals, ','))
          .replace('{{amount}}', withDelimiter(intPart, ',') + '.' + decPart);
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
